import { useEffect, useState } from 'react';
import { Permission, has, decodePermissions } from '@callzone/shared';
import { useActiveChannel, useActiveGuild, useStore } from '../store';
import { useCelular } from '../lib/responsivo';
import { voice } from '../lib/voice';
import { GuildRail } from './GuildRail';
import { ChannelSidebar } from './ChannelSidebar';
import { ChatView } from './ChatView';
import { MemberList } from './MemberList';
import { Stage } from './Stage';
import { InviteModal } from './InviteModal';
import { RolesModal } from './RolesModal';
import { ChannelModal } from './ChannelModal';
import { DevicesModal } from './DevicesModal';
import { PerfilModal } from './PerfilModal';
import { Amigos } from './Amigos';
import { ConversaPrivada } from './ConversaPrivada';
import { MailIcon, MenuIcon, PhoneIcon, PhoneOffIcon, ShieldIcon, UsersIcon, VolumeIcon } from './icons';

export type ModalKind =
  | { kind: 'invites' }
  | { kind: 'roles' }
  | { kind: 'devices' }
  | { kind: 'perfil' }
  | { kind: 'channel'; type: 'text' | 'voice' }
  | null;

export function Workspace() {
  const guild = useActiveGuild();
  const channel = useActiveChannel();
  const voiceSession = useStore((s) => s.voice);
  const [modal, setModal] = useState<ModalKind>(null);
  const celular = useCelular();
  // No computador a lista de membros fica aberta; no celular ela taparia o chat.
  const [showMembers, setShowMembers] = useState(!celular);
  const [showSidebar, setShowSidebar] = useState(false);
  const activeChannelId = useStore((s) => s.activeChannelId);
  const activeDmId = useStore((s) => s.activeDmId);
  const painelAmigos = useStore((s) => s.painelAmigos);

  /*
   * Ao girar o aparelho ou redimensionar a janela, as gavetas precisam voltar a
   * um estado coerente: no computador a lista de membros reaparece, no celular
   * tudo fecha para o chat ocupar a tela.
   */
  useEffect(() => {
    setShowMembers(!celular);
    setShowSidebar(false);
  }, [celular]);

  /*
   * Escolher um canal no celular tem de fechar a gaveta, senao a pessoa clica e
   * continua olhando para a lista de canais, sem entender que ja mudou.
   */
  useEffect(() => {
    if (celular) setShowSidebar(false);
  }, [activeChannelId, celular]);

  const permissions = decodePermissions(guild?.permissions);

  /*
   * Sem guild ainda da para usar amigos e conversas: amizade e entre pessoas,
   * nao entre membros de um servidor. Por isso o painel continua acessivel.
   */
  if (!guild) {
    return (
      <>
        <div className="app-body">
          <GuildRail />
          {painelAmigos && <Amigos />}
          {activeDmId ? (
            <ConversaPrivada />
          ) : (
            <div className="empty-state">
              <div>
                <h2 style={{ marginBottom: 8 }}>Voce ainda nao esta em nenhuma guild</h2>
                <p>Peca um convite a alguem, ou crie a sua pelo botao + na barra lateral.</p>
              </div>
            </div>
          )}
        </div>
        <ChamadaChegando />
      </>
    );
  }

  // Canal de voz aberto mostra o palco; canal de texto mostra o chat.
  const showingStage = channel?.type === 'voice';

  return (
    <>
      <div className={`app-body ${celular ? 'celular' : ''}`}>
        {/* No celular as duas colunas da esquerda viram uma gaveta so. */}
        <div className={`left-drawer ${showSidebar ? 'open' : ''}`}>
          <GuildRail />
          {painelAmigos ? (
            <Amigos onClose={() => setShowSidebar(false)} />
          ) : (
            <ChannelSidebar onOpenModal={setModal} onClose={() => setShowSidebar(false)} />
          )}
        </div>

        {/* Conversa privada aberta substitui o canal da guild na area central. */}
        {activeDmId ? (
          <ConversaPrivada />
        ) : (
        <div className="main">
          <div className="main-header">
            {celular && (
              <button
                className="icon-btn"
                title="Canais"
                aria-label="Abrir canais"
                onClick={() => setShowSidebar(true)}
              >
                <MenuIcon />
              </button>
            )}
            <div className="title">
              <span style={{ color: 'var(--text-dim)', display: 'inline-flex' }}>
                {showingStage ? <VolumeIcon style={{ width: 16, height: 16 }} /> : '#'}
              </span>
              {channel?.name ?? 'Sem canal'}
            </div>
            {channel?.topic && <div className="topic">{channel.topic}</div>}
            <div className="spacer" />
            {has(permissions, Permission.CREATE_INVITE) && (
              <button
                className="icon-btn"
                title="Convites"
                onClick={() => setModal({ kind: 'invites' })}
              >
                <MailIcon />
              </button>
            )}
            {has(permissions, Permission.MANAGE_ROLES) && (
              <button className="icon-btn" title="Cargos" onClick={() => setModal({ kind: 'roles' })}>
                <ShieldIcon />
              </button>
            )}
            <button
              className={`icon-btn ${showMembers ? 'on' : ''}`}
              title="Membros"
              onClick={() => setShowMembers((v) => !v)}
            >
              <UsersIcon />
            </button>
          </div>

          {showingStage ? <Stage /> : channel ? <ChatView /> : (
            <div className="empty-state">Selecione um canal na barra lateral.</div>
          )}
        </div>
        )}

        {showMembers && !activeDmId && (
          <div className="right-drawer open">
            <MemberList onClose={() => setShowMembers(false)} />
          </div>
        )}

        {/*
         * Fundo escuro que fecha a gaveta ao toque. So no celular: no
         * computador as colunas convivem e nao ha nada para fechar.
         */}
        {celular && (showSidebar || showMembers) && (
          <button
            className="drawer-backdrop"
            aria-label="Fechar"
            onClick={() => {
              setShowSidebar(false);
              setShowMembers(false);
            }}
          />
        )}
      </div>

      {modal?.kind === 'invites' && <InviteModal onClose={() => setModal(null)} />}
      {modal?.kind === 'roles' && <RolesModal onClose={() => setModal(null)} />}
      {modal?.kind === 'devices' && <DevicesModal onClose={() => setModal(null)} />}
      {modal?.kind === 'perfil' && <PerfilModal onClose={() => setModal(null)} />}
      {modal?.kind === 'channel' && (
        <ChannelModal type={modal.type} onClose={() => setModal(null)} />
      )}

      <ChamadaChegando />

      {/* Mantem o audio tocando mesmo com o usuario lendo outro canal. */}
      {voiceSession.channelId && !showingStage && !activeDmId && <FloatingVoiceBar />}
    </>
  );
}

/**
 * Aviso de ligacao privada. Nao entra na chamada sozinho de proposito: abrir o
 * microfone de alguem sem a pessoa mandar seria uma falha de privacidade, nao
 * uma comodidade.
 */
function ChamadaChegando() {
  const chamando = useStore((s) => s.chamando);
  const conversas = useStore((s) => s.conversas);
  const setChamando = useStore((s) => s.setChamando);
  const selectDm = useStore((s) => s.selectDm);

  if (!chamando) return null;
  const conversa = conversas.find((c) => c.id === chamando.channelId);

  return (
    <div className="chamada-chegando">
      <div className="chamada-quem">
        <strong>{conversa?.user.nickname ?? 'Alguem'}</strong> está te ligando
      </div>
      <button
        className="btn small"
        onClick={() => {
          selectDm(chamando.channelId);
          setChamando(null);
          void voice.ligarNoPrivado(chamando.channelId);
        }}
      >
        <PhoneIcon style={{ width: 15, height: 15 }} />
        Atender
      </button>
      <button className="btn ghost small" onClick={() => setChamando(null)}>
        Dispensar
      </button>
    </div>
  );
}

/**
 * Barra que aparece quando voce esta em call mas lendo outro canal. Antes so
 * levava de volta ao canal — para desligar era preciso navegar ate la primeiro.
 * Agora o desligar esta aqui, que e onde a pessoa lembra da call.
 */
function FloatingVoiceBar() {
  const voiceSession = useStore((s) => s.voice);
  const guilds = useStore((s) => s.guilds);
  const channelName =
    (voiceSession.guildId &&
      guilds[voiceSession.guildId]?.channels.find((c) => c.id === voiceSession.channelId)?.name) ||
    'canal de voz';
  const selectChannel = useStore((s) => s.selectChannel);

  return (
    <div className="floating-voice">
      <button
        className="floating-voice-go"
        onClick={() => voiceSession.channelId && selectChannel(voiceSession.channelId)}
      >
        <span className="dot" />
        <span>Voz conectada em {channelName}</span>
      </button>
      <button
        className="floating-voice-hangup"
        title="Sair da chamada"
        aria-label="Sair da chamada"
        onClick={() => void voice.leave(true)}
      >
        <PhoneOffIcon />
      </button>
    </div>
  );
}
